import { VendorNetworkModal } from '@components/VendorNetworkModal';
import { calculateCategoryCounts } from '@components/VendorNetworkModal/utils/categoryCounts';
import AnalyticsConstants from '@constants/analytics';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useAppSelector } from '@redux/hooks';
import {
	CtaButton,
	FullscreenModal,
	TextBody,
	TextHero,
} from '@tkww/orion-web-ds';
import type { Raw } from '@typings/vendor';
import { compose } from '@xo-union/react-css-modules';
import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo, useState } from 'react';
import {
	storefrontsNetworkDataAtom,
	vendorNetworkCountAtom,
} from '../../../../jotai/storefront/storefronts-network-pvrec';
import ModalOverrides from './FullscreenModalOverrides.scss';
import { CelebrationStorefrontsPhotoGrid } from './components/CelebrationStorefrontsPhotoGrid';
import Styles from './styles.scss';
import { usePrepareVendorsForGrid } from './utils/transformNetworkData';

const modalClasses = compose({
	content: ModalOverrides.content,
	header: ModalOverrides.header,
});

// Helper functions for VRM tracking
const getSourcePage = (vendor?: Raw): string => {
	return vendor?.purchaseStatus === 'PAID'
		? 'paid storefront'
		: 'unpaid storefront';
};

interface BookVenueSuccessModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const BookVenueSuccessModal: React.FC<BookVenueSuccessModalProps> = ({
	isOpen,
	onClose,
}) => {
	const vendor = useAppSelector((state) => state.vendor.vendorRaw);
	const storefrontsNetworkCount = useAtomValue(vendorNetworkCountAtom);
	const [showVendorNetworkModal, setShowVendorNetworkModal] = useState(false);
	const vendorNetwork = usePrepareVendorsForGrid();
	const { track } = useAnalyticsContext();
	const storefrontsNetworkData = useAtomValue(storefrontsNetworkDataAtom);
	const { recommendationCount, vendorNetworkRecCount } = useMemo(
		() => calculateCategoryCounts(storefrontsNetworkData.network),
		[storefrontsNetworkData.network],
	);

	const trackCTAClick = useCallback((): void => {
		track({
			event: AnalyticsConstants.CTA_CLICKED,
			properties: {
				sourcePage: 'storefront',
				action: 'build your team',
				selection: 'open',
				reason: 'open vendor network',
				vendorNetworkRecCount,
				recommendationCount,
			},
		});
	}, [track, vendorNetworkRecCount, recommendationCount]);

	const handleCtaClick = () => {
		trackCTAClick();
		setShowVendorNetworkModal(true);
	};

	const handleCloseVendorNetworkModal = () => {
		setShowVendorNetworkModal(false);
		onClose();
	};

	if (showVendorNetworkModal) {
		return (
			<VendorNetworkModal
				isOpen={showVendorNetworkModal}
				onClose={handleCloseVendorNetworkModal}
			/>
		);
	}

	return (
		<FullscreenModal
			isOpen={isOpen}
			onClose={onClose}
			showLogo={false}
			classes={modalClasses}
		>
			<div
				className={`${Styles.container} book-venue-success-container`}
				data-testid="book-venue-success-modal"
			>
				<div
					className={Styles.contentWrapper}
					data-testid="book-venue-success-content"
				>
					<TextHero
						size="md"
						className={Styles.title}
						data-testid="book-venue-success-title"
					>
						Congrats, you've got the place!
					</TextHero>
					<TextBody
						size="md"
						className={Styles.subtitle}
						data-testid="book-venue-success-subtitle"
					>
						Now, start building your team with vendors who already know your
						venue.
					</TextBody>
					<div
						className={Styles.vendorImageContainer}
						data-testid="book-venue-success-image-container"
					>
						<CelebrationStorefrontsPhotoGrid vendors={vendorNetwork} />
					</div>

					<TextBody
						size="md"
						className={Styles.vendorNetworkDescription}
						data-testid="book-venue-success-network-description"
					>
						Meet vendors who've helped couples celebrate at{' '}
						<span
							className={Styles.vendorName}
							data-testid="book-venue-success-vendor-name"
						>
							{vendor?.name}
						</span>
						.
					</TextBody>
				</div>
				<div
					className={Styles.actionsWrapper}
					data-testid="book-venue-success-actions"
				>
					<CtaButton
						as="button"
						treatAs="button"
						styleType="primary"
						size="lg"
						onClick={handleCtaClick}
						className={Styles.ctaButton}
						data-testid="book-venue-success-cta-button"
					>
						Start building your team
					</CtaButton>
				</div>
			</div>
		</FullscreenModal>
	);
};
